import { useCallback, useEffect, useState } from "react";

// Site key is configured in index.html where the script is loaded
const RECAPTCHA_SITE_KEY = "6Le6ozAsAAAAANwmz4dfJbbR4iYC3zIfRAGznDR_";

declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

/**
 * Custom hook for Google reCAPTCHA v3 integration.
 * The reCAPTCHA script is loaded from index.html for faster initialization.
 *
 * Usage:
 *   const { getRecaptchaToken, isReady } = useRecaptcha();
 *
 *   const handleSubmit = async () => {
 *     const token = await getRecaptchaToken('magic_link');
 *     // Include token in your API request
 *   };
 */
export function useRecaptcha() {
  const [isReady, setIsReady] = useState(false);

  // Check if reCAPTCHA script is loaded (script is loaded from index.html)
  useEffect(() => {
    // Check if script is already loaded and ready
    if (window.grecaptcha?.enterprise) {
      setIsReady(true);
      return;
    }

    // Poll for script to be ready (script is loaded async from index.html)
    const interval = setInterval(() => {
      if (window.grecaptcha?.enterprise) {
        setIsReady(true);
        clearInterval(interval);
      }
    }, 100);

    // Cleanup interval after 10 seconds max
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.grecaptcha?.enterprise) {
        console.error("reCAPTCHA script failed to load");
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  /**
   * Get a reCAPTCHA token for the specified action.
   * Returns null if reCAPTCHA is not loaded.
   */
  const getRecaptchaToken = useCallback(
    async (action: string): Promise<string | null> => {
      if (!isReady || !window.grecaptcha?.enterprise) {
        console.warn("reCAPTCHA Enterprise script not yet loaded");
        return null;
      }

      try {
        return new Promise((resolve, reject) => {
          window.grecaptcha.enterprise.ready(async () => {
            try {
              const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, {
                action,
              });
              resolve(token);
            } catch (error) {
              console.error("Failed to execute reCAPTCHA Enterprise:", error);
              reject(error);
            }
          });
        });
      } catch (error) {
        console.error("reCAPTCHA error:", error);
        return null;
      }
    },
    [isReady]
  );

  return {
    getRecaptchaToken,
    isReady,
  };
}
